// Libraries
import * as d3 from 'd3';

// Styles
import { theme } from '../utils/Theme';

//Types
import { EquityIndicator, ChangeYear, Indicator } from '../utils/Types';

const selector = "root";

export function init() {
    const tooltip = d3.select(`#${selector}`)
            .append('div')
            .attr('class', 'tooltip')
            .style('max-width', '200px')
            .style('position', 'absolute')
            .style('top', '0px')
            .style('left', '0px')
            .style('visibility', 'hidden')
            .style('padding', '6px')
            .style('pointer-events', 'none')
            .style('border-radius', '5px')
            .style('font-weight', 'normal')
            .style('font-family', theme.fontFamily)
            .style('font-size', `${theme.fontSize}px`)
            .style('color', theme.grey.primary)
            .style('line-height', theme.lineHeight)
            .style('box-shadow', 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px')
            .style('z-index', 1000);

    return tooltip;
}

export function pointerOver(x: number, y: number, str: string, grey?: boolean) {

    const nav: HTMLElement | null = d3.select(`.Navigation`).node() as HTMLElement | null;
    const navHeight = nav?.getBoundingClientRect().height;

    y = y + navHeight! - 20;

    d3.select(`#${selector} .tooltip`)
        .style('visibility', 'visible')
        .style('top', `${y}px`)
        .style('left', `${x}px`)
        .style('background-color', grey ? 'rgba(240,238,236, .9)':'rgba(250, 246, 240, .85)')
        .style('border', grey ? `1.5px solid #B7B7B7`: `1.5px solid ${theme.grey.primary}`)
        .html(str);
}

export function pointerOut() {
    d3.select(`#${selector} .tooltip`)
        .style('visibility', 'hidden');
}

export function mouseOverGeo(feature: any) {
    return `<div class="ComponentGroupInner SemiBold">${feature.properties.name} ${feature.properties.descr}</div>`;
}

export function returnSpecificEquityIndicator(feature: any, equityIndicator: EquityIndicator) {
    return feature.properties!.changeYearData[equityIndicator.variable];
}

function mouseOverEquityIndicator(feature: GeoJSON.Feature, equityIndicator: EquityIndicator, changeYear: ChangeYear, showEquityIndicator: boolean) {

    const stfp = feature.properties?.stfp;
    if (feature.properties === null || feature.properties.changeYearData === undefined) {
        return `<div class="DetailInformation"><span class="SemiBold">Missing population data</span> in base year ${baseYearDescr(stfp, changeYear)}</div>`;
    } else {
        const ei = returnSpecificEquityIndicator(feature, equityIndicator);
        if (showEquityIndicator) {
            if (equityIndicator.variable !== 'baseYearPopDensity') {
                return `<div class="DetailInformation"><span class="SemiBold focusColor">${ei.equityMeasure}${equityIndicator.descr}</span> in base year ${baseYearDescr(stfp, changeYear)}</div>`;
            } else {
                return `<div class="DetailInformation"><span class="SemiBold focusColor">${ei.equityMeasure}</span> people per square mile in base year ${baseYearDescr(stfp, changeYear)}</div>`;
            }
        } else {
            return '';
        }
    }
}

export function mouseOverTextPoll(d: any) {
    return `<div class="ComponentGroupInner SemiBold">${d.name}</div><div>Status:<span class="${d.status} SemiBold"> ${d.status}</span></div>`;
}

// todo update back to `${mouseOverGeo(feature)}` when we have more information on the state to add here
export function mouseOverTextVD(feature: any) {
    return `<div class="SemiBold">${feature.properties.name} ${feature.properties.descr}</div>`
}

// Updates each state to give more information about the specific change year
export function changeYearDescr(stfp: string, changeYear: any) {

    if (changeYear.changeYear === "Overall Change") {

        if (stfp == '13') {
            return "2016 – 2022";
        } else if (['39', '22'].includes(stfp)) {
            return "2012 – 2020";
        } else {
            return "2012 – 2022";
        }

    } else {
        return changeYear.changeYear;
    }
}

// Updates each state to give more information about the specific base year
export function baseYearDescr(stfp: string, changeYear: any) {

    if (changeYear.changeYear === "Overall Change") {

        if (['39', '22'].includes(stfp)) {
            return "2020";
        } else {
            return "2022";
        }

    } else {
        return changeYear.baseYear;
    }
}

// Updates each state to give more information about the specific last general election year
export function lastGeYearDescr(stfp: string, changeYear: any) {

    if (changeYear.changeYear === "Overall Change") {

        if (['13'].includes(stfp)) {
            return "2016";
        } else {
            return "2012";
        }

    } else {
        return changeYear.geYear;
    }
}


function getNetChange(status: any, changeYear: ChangeYear, stfp: string) {

    if (status?.overallChange === 0) {
        return `<div class="DetailInformation"><span class="SemiBold">No change</span> in the net # of polls between ${changeYearDescr(stfp, changeYear)}</div>`;
    } else {
        const overallStatus =  status?.overallChange > 0? 'gain': 'loss';
        const color =  status?.overallChange > 0? 'Added': 'Removed';
        return `<div class="DetailInformation">Net<span class="SemiBold ${color}"> ${overallStatus} of ${Math.abs(status?.overallChange ?? 0)} </span> poll locations between ${changeYearDescr(stfp, changeYear)}</div>`;
    }
}

function getNetChangeCVAPPerPoll(status: any, changeYear: ChangeYear, stfp: string) {

    if (Math.round(status.changeYearCVAPPerPoll) <= 50 && Math.round(status.changeYearCVAPPerPoll) >= -50) {
        return `<div class="DetailInformation"><span class="SemiBold">No change</span> in the CVAP per poll between ${changeYearDescr(stfp, changeYear)}</div>`;
    } else if (Math.round(status.changeYearCVAPPerPoll) > 50) {
        return `<div class="DetailInformation"><span class="SemiBold Removed">Increase</span> of ${Math.round(status.changeYearCVAPPerPoll)} CVAP per poll between ${changeYearDescr(stfp, changeYear)}</div>`;
    } else {
        return `<div class="DetailInformation"><span class="SemiBold Added">Decrease</span> of ${Math.abs(Math.round(status.changeYearCVAPPerPoll))} CVAP per poll between ${changeYearDescr(stfp, changeYear)}</div>`;
    }
}

function getBaseYearCVAPPerPoll(status: any, changeYear: ChangeYear, stfp: string) {
    return `<div class="DetailInformation"><span class="SemiBold">${Math.round(status?.baseYearCVAPPerPoll)}</span> CVAP per poll in ${baseYearDescr(stfp, changeYear)}</div>`;
}

function getTotalNoPolls(status: any, changeYear: ChangeYear, stfp: string) {
    return `<div class="DetailInformation"><span class="SemiBold">${status?.baseYearPollingLocationsTotal}</span> total polling locations in base year ${baseYearDescr(stfp, changeYear)}</div>`
}

function missingDataFlag(changeYear: ChangeYear, stfp: string) {
    return `<div class="DetailInformation">Missing too many polling locations between ${changeYearDescr(stfp, changeYear)} to show data</div>`;
}

// function dataAlertMissingPolls(changeYear: ChangeYear, stfp: string) {

//     if (status?.baseYearNoPollsMissingLatLng > 0 || status?.lastGEYearNoPollsMissingLatLng > 0) {

//         const str = missingDataFlag(changeYear, stfp);
//         return `<div class="DataWarning"><div class="Detail">${warningSVG()}${str}</div></div>`;
//     } else {
//         return "";
//     }

// }

function warningSVG() {
    return `<svg class="MuiSvgIcon-fontSizeSmall WarningIcon" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="WarningIcon"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path></svg>`
}

function resetSVG() {
    return `<svg width="16" height="14" viewBox="0 0 16 14" xmlns="http://www.w3.org/2000/svg"><path d="M9.21956 13.5609C8.31754 13.5609 7.46462 13.3934 6.66079 13.0585C5.85696 12.7235 5.1421 12.26 4.51621 11.6679L5.37679 10.815C5.89222 11.2967 6.47337 11.6753 7.12022 11.9509C7.76708 12.2265 8.46686 12.3644 9.21956 12.3644C10.7669 12.3644 12.0844 11.8205 13.172 10.7329C14.2596 9.64527 14.8035 8.32779 14.8035 6.78044C14.8035 5.23822 14.2584 3.92201 13.1682 2.83182C12.078 1.74163 10.7618 1.19653 9.21956 1.19653C7.67733 1.19653 6.36112 1.74163 5.27093 2.83182C4.18074 3.92201 3.63564 5.23822 3.63564 6.78044V7.40331L5.22185 5.8171L6.07476 6.65006L3.03739 9.68743L0 6.65006L0.852922 5.8171L2.43911 7.42325V6.78044C2.43911 5.83917 2.61707 4.95751 2.97297 4.13547C3.32887 3.31342 3.81259 2.59661 4.42415 1.98504C5.03572 1.37348 5.75248 0.889757 6.57444 0.533862C7.39639 0.177954 8.27795 0 9.21912 0C10.1603 0 11.042 0.177954 11.8642 0.533862C12.6865 0.889757 13.4034 1.37348 14.015 1.98504C14.6265 2.59661 15.1102 3.31335 15.4661 4.13525C15.822 4.95714 16 5.83864 16 6.77975C16 8.66196 15.3408 10.2627 14.0224 11.582C12.7039 12.9012 11.103 13.5609 9.21956 13.5609Z" fill="#757575"/></svg>`
}

function getMissingPollsBaseYear(status: any, changeYear: ChangeYear, stfp: string) {
    return `<div>Missing location data for ${status?.baseYearNoPollsMissingLatLng} ${status?.baseYearNoPollsMissingLatLng > 1? "polls": "poll"} in ${baseYearDescr(stfp, changeYear)}</div>`;
}

function getMissingPollsGEYear(status: any, changeYear: ChangeYear, stfp: string) {
    return `<div> Missing location data for ${status?.lastGEYearNoPollsMissingLatLng} ${status?.lastGEYearNoPollsMissingLatLng > 1? "polls": "poll"} in ${lastGeYearDescr(stfp, changeYear)}</div>`;
}

function getMissingPollsBothYears(status: any, changeYear: ChangeYear, stfp: string) {
    return `<div> Missing location data for ${status?.baseYearNoPollsMissingLatLng} ${status?.baseYearNoPollsMissingLatLng > 1? "polls": "poll"} in ${baseYearDescr(stfp, changeYear)} and ${status?.lastGEYearNoPollsMissingLatLng} ${status?.lastGEYearNoPollsMissingLatLng > 1? "polls": "poll"} in ${lastGeYearDescr(stfp, changeYear)}</div>`;
}

function dataWarningMissingPolls(status: any, changeYear: ChangeYear, stfp: string) {

    if (status?.baseYearNoPollsMissingLatLng > 0 || status?.lastGEYearNoPollsMissingLatLng > 0) {

        let str;
        if (status?.baseYearNoPollsMissingLatLng > 0 && status?.lastGEYearNoPollsMissingLatLng > 0) {
            str = getMissingPollsBothYears(status, changeYear, stfp);
        } else if (status?.baseYearNoPollsMissingLatLng > 0) {
            str = getMissingPollsBaseYear(status, changeYear, stfp);
        } else if(status?.lastGEYearNoPollsMissingLatLng > 0) {
            str = getMissingPollsGEYear(status, changeYear, stfp);
        } else {
            str = "";
        }
        return `<div class="DataWarning"><div class="Detail">${warningSVG()}${str}</div></div>`
    } else {
        return "";
    }
}

function dataResetWarning() {
    return `<div class="Detail">Not included in current selection. Under the Layers panel select a new state or under the Filters panel adjust the slider ranges, or use reset button, ${resetSVG()}, to make this county clickable.</div>`
}

export function mouseOverTextTract(feature: any, equityIndicator: EquityIndicator, changeYear: ChangeYear, showEquityIndicator: boolean) {

    const tractName = `<div class="ComponentGroupInner SemiBold">${feature.properties.descr} ${feature.properties.name}</div>`;
    const ei = `${mouseOverEquityIndicator(feature, equityIndicator, changeYear, showEquityIndicator)}`;
    const stfp = feature.properties?.stfp;

    if (feature.properties.changeYearData === undefined) {
        return `${tractName}${ei}${missingDataFlag(changeYear, stfp)}`;
    } else {
        if (feature.properties.changeYearData.pollSummary.missingDataFlag) {
            return `${tractName}${ei}${missingDataFlag(changeYear, stfp)}`;
        } else {        
            const pollSummary = feature.properties.changeYearData.pollSummary;
            const netChange = getNetChange(pollSummary.status, changeYear, stfp);
            const totalNoPolls = getTotalNoPolls(pollSummary.status, changeYear, stfp);
            const pollsAdded = pollSummary.status.pollsAdded > 0 ? `<div class="DetailInformation"><span class="Added SemiBold">${pollSummary.status.pollsAdded} poll added </span> in ${baseYearDescr(stfp, changeYear)}</div>`: ``;
            const pollsRemoved = pollSummary.status.pollsRemoved > 0 ? `<div class="DetailInformation"><span class="Removed SemiBold">${pollSummary.status.pollsRemoved} poll removed </span> in ${baseYearDescr(stfp, changeYear)}</div>`: ``;
            const pollsNoChange = pollSummary.status.pollsNoChange > 0 ? `<div class="DetailInformation"><span class="SemiBold">${pollSummary.status.pollsNoChange} polls stayed the same </span> in ${baseYearDescr(stfp, changeYear)}</div>`: ``;
            return `${tractName}${ei}${totalNoPolls}${netChange}${pollsAdded}${pollsRemoved}${pollsNoChange}`;
        }
    }
}

export function mouseOverTextCounty(feature: any, equityIndicator: EquityIndicator, changeYear: ChangeYear, showEquityIndicator: boolean, indicator: Indicator) {

    const countyName = mouseOverGeo(feature);
    const stfp = feature.properties?.stfp;

    if (feature.properties.changeYearData === undefined) {
        return `${countyName}${missingDataFlag(changeYear, stfp)}`;
    } else {
        const ei = `${mouseOverEquityIndicator(feature, equityIndicator, changeYear, showEquityIndicator)}`;
        const pollSummary = feature.properties.changeYearData.pollSummary.status;

        if (feature.properties.changeYearData.pollSummary.missingDataFlag || !feature.properties.selected) {
            if (feature.properties.changeYearData.pollSummary.missingDataFlag) {
                return `${countyName}${ei}${missingDataFlag(changeYear, stfp)}`;
            } else if (!feature.properties.selected) {
                return dataResetWarning();
            } else {
                return "";
            }
        } else {
            const missingPolls = dataWarningMissingPolls(pollSummary, changeYear, stfp);
            if (indicator.variable === 'changeNoPolls') {
                const noChanges = `<div class="DetailInformation"><span class="SemiBold">${pollSummary?.changeNoPolls}</span> poll locations changed between ${changeYearDescr(stfp, changeYear)}</div>`;
                const totalPolls = getTotalNoPolls(pollSummary, changeYear, stfp);
                const netChange = getNetChange(pollSummary, changeYear, stfp);
                return `${countyName}${ei}${totalPolls}${noChanges}${netChange}${missingPolls}`;
            } else {
                const changeYearCVAPPerPoll = getNetChangeCVAPPerPoll(pollSummary, changeYear, stfp);
                const baseYearCVAPPerPoll = getBaseYearCVAPPerPoll(pollSummary, changeYear, stfp);
                return `${countyName}${ei}${baseYearCVAPPerPoll}${changeYearCVAPPerPoll}${missingPolls}`;
            }
        }
    }    
}

// keeping two functions in case we need to distinguish between the two tooltips
export function mouseOverTextPollSummary(feature: any, equityIndicator: EquityIndicator, changeYear: ChangeYear, showEquityIndicator: boolean, indicator: Indicator) {
    return mouseOverTextCounty(feature, equityIndicator, changeYear, showEquityIndicator, indicator);
}

// todo update back to `${mouseOverGeo(feature)}` when we have more information on the state to add here
export function mouseOverTextState(feature: any) {
    return `<div class="SemiBold">${feature.properties.name} ${feature.properties.descr}</div>`
}

export function CVAPListener() {
    d3.selectAll(".CVAP")
        .on('mouseover', (event: any) => {
            let html = "<div>Citizen Voting Age Population (CVAP)</div>";
            return pointerOver(event.clientX - 25, event.clientY -115, html, true)
        })
        .on('mouseout', () => pointerOut());
}

export function InfoIconListener() {
    d3.selectAll(".PriorityHighIcon")
        .on('mouseover', (event: any) => {
            let html =  "<div>Overall change is between 2012 – 2022 except in <span class='SemiBold'>Georgia</span> (2016 – 2022), <span class='SemiBold'>Louisiana</span> and <span class='SemiBold'>Ohio</span> (2012 – 2020). Year options also differ in these states depending on data availability.</div>";
            return pointerOver(event.clientX + 12, event.clientY -100, html, true)
        })
        .on('mouseout', () => pointerOut());
}
